import React, { useState, useEffect, useRef } from 'react';
import { motion } from 'framer-motion';
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from 'recharts';

const STORAGE_KEY = 'ggt_subscribed';

/**
 * Interactive Energy Supply Visualization
 * Shows the world's future energy supply with clickable filters and custom tooltips
 */
const EnergySupplyChart = () => {
  // Subscription state for gating detailed insights
  const [isSubscribed, setIsSubscribed] = useState(false);
  const [showGate, setShowGate] = useState(false);
  const formContainerRef = useRef(null);

  useEffect(() => {
    const subscribed = localStorage.getItem(STORAGE_KEY);
    if (subscribed === 'true') {
      setIsSubscribed(true);
    }
  }, []);

  useEffect(() => {
    if (showGate && !isSubscribed && formContainerRef.current) {
      const existingScript = formContainerRef.current.querySelector('script[data-uid="f63efd6bac"]');
      if (!existingScript) {
        const script = document.createElement('script');
        script.async = true;
        script.setAttribute('data-uid', 'f63efd6bac');
        script.src = 'https://greengasturbines.kit.com/f63efd6bac/index.js';
        formContainerRef.current.appendChild(script);
      }

      // Check for subscription success
      const checkForSubscription = () => {
        const successIndicators = formContainerRef.current?.querySelectorAll('.formkit-alert-success, [data-element="success"]');
        if (successIndicators && successIndicators.length > 0) {
          localStorage.setItem(STORAGE_KEY, 'true');
          setIsSubscribed(true);
          setShowGate(false);
        }
      };

      const interval = setInterval(checkForSubscription, 1000);
      const observer = new MutationObserver(checkForSubscription);
      observer.observe(formContainerRef.current, { childList: true, subtree: true, attributes: true });

      return () => {
        clearInterval(interval);
        observer.disconnect();
      };
    }
  }, [showGate, isSubscribed]);

  // Energy data in Exajoules (EJ) - based on the reference image
  const energyData = [
    {
      year: 2024,
      Oil: 178,
      NaturalGas: 148,
      Coal: 165,
      Other: 65,
      Solar: 45,
      Nuclear: 31,
      Wind: 40,
      Hydro: 19,
      Bio: 13,
    },
    {
      year: 2035,
      Oil: 192,
      NaturalGas: 165,
      Coal: 143,
      Other: 43,
      Solar: 79,
      Nuclear: 40,
      Wind: 65,
      Hydro: 19,
      Bio: 8,
    },
    {
      year: 2050,
      Oil: 184,
      NaturalGas: 161,
      Coal: 95,
      Other: 91,
      Solar: 79,
      Nuclear: 61,
      Wind: 40,
      Hydro: 13,
      Bio: 8,
    },
  ];

  // Color mapping for each energy source
  const energyColors = {
    Oil: '#9370DB',
    NaturalGas: '#4ADE80',
    Coal: '#000000',
    Other: '#94A3B8',
    Solar: '#FDE047',
    Nuclear: '#F9A8D4',
    Wind: '#86EFAC',
    Hydro: '#38BDF8',
    Bio: '#BE123C',
  };

  // State to track which energy sources are visible
  const [visibleSources, setVisibleSources] = useState({
    Oil: true,
    NaturalGas: true,
    Coal: true,
    Other: true,
    Solar: true,
    Nuclear: true,
    Wind: true,
    Hydro: true,
    Bio: true,
  });

  // Toggle visibility of an energy source
  const toggleSource = (source) => {
    setVisibleSources((prev) => ({
      ...prev,
      [source]: !prev[source],
    }));
  };

  // Custom tooltip component
  const CustomTooltip = ({ active, payload }) => {
    if (active && payload && payload.length) {
      const year = payload[0].payload.year;
      
      // Calculate total energy for percentage calculation
      const total = payload.reduce((sum, entry) => {
        if (visibleSources[entry.dataKey]) {
          return sum + (entry.value || 0);
        }
        return sum;
      }, 0);

      return (
        <div className="bg-white border-2 border-primary-500 rounded-lg shadow-xl p-4 max-w-xs">
          <p className="font-bold text-secondary-900 mb-3 text-lg border-b pb-2">
            {year}
          </p>
          <div className="space-y-2">
            {payload
              .filter((entry) => visibleSources[entry.dataKey])
              .sort((a, b) => b.value - a.value)
              .map((entry) => {
                const percentage = ((entry.value / total) * 100).toFixed(1);
                return (
                  <div key={entry.dataKey} className="flex items-center justify-between gap-4">
                    <div className="flex items-center gap-2">
                      <div
                        className="w-3 h-3 rounded-full"
                        style={{ backgroundColor: entry.color }}
                      />
                      <span className="text-sm font-medium text-secondary-700">
                        {entry.dataKey}
                      </span>
                    </div>
                    <div className="text-right">
                      <span className="text-sm font-bold text-secondary-900">
                        {entry.value} EJ
                      </span>
                      <span className="text-xs text-secondary-500 ml-2">
                        ({percentage}%)
                      </span>
                    </div>
                  </div>
                );
              })}
          </div>
          <div className="mt-3 pt-3 border-t">
            <div className="flex justify-between">
              <span className="text-sm font-bold text-secondary-900">Total:</span>
              <span className="text-sm font-bold text-primary-600">{total} EJ</span>
            </div>
          </div>
        </div>
      );
    }
    return null;
  };

  // Custom legend component with click functionality
  const CustomLegend = () => {
    return (
      <div className="flex flex-wrap justify-center gap-3 mt-8 mb-4">
        {Object.keys(energyColors).map((source) => (
          <motion.button
            key={source}
            onClick={() => toggleSource(source)}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg border-2 transition-all duration-200 ${
              visibleSources[source]
                ? 'border-transparent bg-white shadow-md hover:shadow-lg'
                : 'border-dashed border-gray-300 bg-gray-50 opacity-50 hover:opacity-75'
            }`}
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
          >
            <div
              className={`w-4 h-4 rounded-full ${
                visibleSources[source] ? '' : 'opacity-30'
              }`}
              style={{ backgroundColor: energyColors[source] }}
            />
            <span
              className={`text-sm font-medium ${
                visibleSources[source]
                  ? 'text-secondary-900'
                  : 'text-secondary-400 line-through'
              }`}
            >
              {source}
            </span>
          </motion.button>
        ))}
      </div>
    );
  };

  return (
    <section className="py-20 bg-gradient-to-b from-gray-50 to-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Heading */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
          className="text-center mb-12"
        >
          <h2 className="text-4xl md:text-5xl font-bold text-secondary-900 mb-6">
            The World's Future Energy Supply
          </h2>
          <p className="text-xl text-secondary-600 max-w-3xl mx-auto">
            Explore how <strong className="text-primary-600">Natural Gas</strong> plays
            a crucial role in the global energy transition. Click on any energy source
            below to visualize a future without it.
          </p>
        </motion.div>

        {/* Interactive Chart */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8, delay: 0.2 }}
          className="bg-white rounded-2xl shadow-xl p-8 border border-gray-100"
        >
          <ResponsiveContainer width="100%" height={500}>
            <AreaChart
              data={energyData}
              margin={{ top: 10, right: 30, left: 0, bottom: 0 }}
            >
              <defs>
                {Object.entries(energyColors).map(([source, color]) => (
                  <linearGradient
                    key={source}
                    id={`color${source}`}
                    x1="0"
                    y1="0"
                    x2="0"
                    y2="1"
                  >
                    <stop offset="5%" stopColor={color} stopOpacity={0.8} />
                    <stop offset="95%" stopColor={color} stopOpacity={0.3} />
                  </linearGradient>
                ))}
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
              <XAxis
                dataKey="year"
                tick={{ fill: '#6b7280', fontSize: 14, fontWeight: 600 }}
                tickLine={{ stroke: '#9ca3af' }}
              />
              <YAxis
                tick={{ fill: '#6b7280', fontSize: 14 }}
                tickLine={{ stroke: '#9ca3af' }}
                label={{
                  value: 'Energy (Exajoules)',
                  angle: -90,
                  position: 'insideLeft',
                  style: { fill: '#374151', fontWeight: 600 },
                }}
              />
              <Tooltip content={<CustomTooltip />} />
              
              {/* Render areas in reverse order so important ones are on top */}
              {visibleSources.Bio && (
                <Area
                  type="monotone"
                  dataKey="Bio"
                  stackId="1"
                  stroke={energyColors.Bio}
                  fill={`url(#colorBio)`}
                  strokeWidth={2}
                />
              )}
              {visibleSources.Hydro && (
                <Area
                  type="monotone"
                  dataKey="Hydro"
                  stackId="1"
                  stroke={energyColors.Hydro}
                  fill={`url(#colorHydro)`}
                  strokeWidth={2}
                />
              )}
              {visibleSources.Wind && (
                <Area
                  type="monotone"
                  dataKey="Wind"
                  stackId="1"
                  stroke={energyColors.Wind}
                  fill={`url(#colorWind)`}
                  strokeWidth={2}
                />
              )}
              {visibleSources.Nuclear && (
                <Area
                  type="monotone"
                  dataKey="Nuclear"
                  stackId="1"
                  stroke={energyColors.Nuclear}
                  fill={`url(#colorNuclear)`}
                  strokeWidth={2}
                />
              )}
              {visibleSources.Solar && (
                <Area
                  type="monotone"
                  dataKey="Solar"
                  stackId="1"
                  stroke={energyColors.Solar}
                  fill={`url(#colorSolar)`}
                  strokeWidth={2}
                />
              )}
              {visibleSources.Other && (
                <Area
                  type="monotone"
                  dataKey="Other"
                  stackId="1"
                  stroke={energyColors.Other}
                  fill={`url(#colorOther)`}
                  strokeWidth={2}
                />
              )}
              {visibleSources.Coal && (
                <Area
                  type="monotone"
                  dataKey="Coal"
                  stackId="1"
                  stroke={energyColors.Coal}
                  fill={`url(#colorCoal)`}
                  strokeWidth={2}
                />
              )}
              {visibleSources.NaturalGas && (
                <Area
                  type="monotone"
                  dataKey="NaturalGas"
                  stackId="1"
                  stroke={energyColors.NaturalGas}
                  fill={`url(#colorNaturalGas)`}
                  strokeWidth={2}
                />
              )}
              {visibleSources.Oil && (
                <Area
                  type="monotone"
                  dataKey="Oil"
                  stackId="1"
                  stroke={energyColors.Oil}
                  fill={`url(#colorOil)`}
                  strokeWidth={2}
                />
              )}
            </AreaChart>
          </ResponsiveContainer>

          {/* Custom Interactive Legend */}
          <CustomLegend />

          {/* Instructions */}
          <motion.div
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8, delay: 0.4 }}
            className="text-center mt-6 p-4 bg-primary-50 rounded-lg border border-primary-200"
          >
            <p className="text-sm text-secondary-700">
              <strong className="text-primary-700">💡 Interactive Chart:</strong> Hover
              over the chart to see exact values and percentages. Click any energy source
              in the legend to remove it from the visualization and explore different
              energy scenarios.
            </p>
          </motion.div>
        </motion.div>

        {/* Insight Box - Gated for non-subscribers */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8, delay: 0.4 }}
          className="mt-12 relative"
        >
          {/* Preview content - always visible */}
          <div className={`bg-gradient-to-r from-primary-600 to-primary-700 rounded-2xl p-8 md:p-12 text-white ${!isSubscribed ? 'pb-32' : ''}`}>
            <div className="max-w-3xl mx-auto text-center">
              <h3 className="text-3xl font-bold mb-4">
                Natural Gas: The Bridge to a Greener Future
              </h3>
              <p className="text-xl text-primary-100 mb-6">
                As the world transitions away from coal, natural gas provides reliable,
                lower-carbon baseload power while renewables scale up. Our hydrogen-ready
                turbines ensure your investment stays relevant for decades.
              </p>
              
              {/* Gated detailed stats */}
              {isSubscribed ? (
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-8">
                  <div className="bg-white/10 backdrop-blur-sm rounded-lg p-4">
                    <div className="text-3xl font-bold text-white mb-2">50%</div>
                    <div className="text-sm text-primary-100">
                      Lower CO₂ than coal
                    </div>
                  </div>
                  <div className="bg-white/10 backdrop-blur-sm rounded-lg p-4">
                    <div className="text-3xl font-bold text-white mb-2">99.5%</div>
                    <div className="text-sm text-primary-100">
                      Reliability for grid stability
                    </div>
                  </div>
                  <div className="bg-white/10 backdrop-blur-sm rounded-lg p-4">
                    <div className="text-3xl font-bold text-white mb-2">100%</div>
                    <div className="text-sm text-primary-100">
                      Hydrogen-ready for zero carbon
                    </div>
                  </div>
                </div>
              ) : (
                <div className="relative mt-8">
                  {/* Blurred preview of stats */}
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-6 blur-md select-none pointer-events-none opacity-50">
                    <div className="bg-white/10 backdrop-blur-sm rounded-lg p-4">
                      <div className="text-3xl font-bold text-white mb-2">XX%</div>
                      <div className="text-sm text-primary-100">Key insight #1</div>
                    </div>
                    <div className="bg-white/10 backdrop-blur-sm rounded-lg p-4">
                      <div className="text-3xl font-bold text-white mb-2">XX%</div>
                      <div className="text-sm text-primary-100">Key insight #2</div>
                    </div>
                    <div className="bg-white/10 backdrop-blur-sm rounded-lg p-4">
                      <div className="text-3xl font-bold text-white mb-2">XX%</div>
                      <div className="text-sm text-primary-100">Key insight #3</div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Subscription Gate Overlay */}
          {!isSubscribed && (
            <div className="absolute bottom-0 left-0 right-0 transform translate-y-1/2">
              <div className="max-w-lg mx-auto">
                {!showGate ? (
                  <motion.button
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    onClick={() => setShowGate(true)}
                    className="w-full bg-white rounded-2xl shadow-2xl p-6 border-2 border-primary-200 hover:border-primary-400 transition-all group"
                  >
                    <div className="flex items-center justify-center gap-3">
                      <div className="w-10 h-10 bg-primary-100 rounded-full flex items-center justify-center group-hover:bg-primary-200 transition-colors">
                        <svg className="w-5 h-5 text-primary-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                        </svg>
                      </div>
                      <div className="text-left">
                        <p className="font-bold text-secondary-900">Unlock Detailed Energy Insights</p>
                        <p className="text-sm text-gray-600">Subscribe to see the full analysis</p>
                      </div>
                      <svg className="w-5 h-5 text-primary-600 group-hover:translate-x-1 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                      </svg>
                    </div>
                  </motion.button>
                ) : (
                  <motion.div
                    initial={{ opacity: 0, scale: 0.95 }}
                    animate={{ opacity: 1, scale: 1 }}
                    className="bg-white rounded-2xl shadow-2xl p-6 border-2 border-primary-200"
                  >
                    <button 
                      onClick={() => setShowGate(false)}
                      className="absolute top-4 right-4 text-gray-400 hover:text-gray-600"
                    >
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </button>
                    <div className="text-center mb-4">
                      <div className="w-12 h-12 bg-primary-100 rounded-full flex items-center justify-center mx-auto mb-3">
                        <svg className="w-6 h-6 text-primary-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                        </svg>
                      </div>
                      <h4 className="font-bold text-secondary-900 text-lg">Get Full Energy Analysis</h4>
                      <p className="text-sm text-gray-600 mt-1">Join 2,500+ energy professionals</p>
                    </div>
                    <div ref={formContainerRef} className="kit-energy-form">
                      {/* Kit form will be injected here */}
                    </div>
                    <p className="text-xs text-gray-500 text-center mt-3">No spam. Unsubscribe anytime.</p>
                  </motion.div>
                )}
              </div>
            </div>
          )}
        </motion.div>

        {/* Spacer for gate overlay */}
        {!isSubscribed && <div className="h-24" />}
      </div>
    </section>
  );
};

export default EnergySupplyChart;
